// generateFAQPageJsonLd.js
import { DEFAULT_LANGUAGE } from '../resource/i18nCommon.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { localeConvertor } from '../resource/i18nCommon.js';
import { getLanguageTranslationKey } from '../resource/getCurrentLanguage.js';
import { formatISO } from 'date-fns';
/**
 * Generate FAQPage JSON-LD structured data
 * @param {Object} options
 * @param {Array<{title: string, body: string, created_at: number, updated_at: number}>} options.faqItems - Array of FAQ items with title, body, created_at, updated_at
 * @param {string} options.pageUrl - Full URL of the FAQ page
 * @param {string} options.origin - Origin URL (default: 'https://swag.live')
 * @param {Array} options.supportedLanguages - Supported languages
 * @param {Object} options.i18n - i18n instance
 * @returns {Object} FAQPage JSON-LD object
 */
const generateFAQPageJsonLd = async ({
  faqItems = [],
  pageUrl = 'https://swag.live/settings/faq',
  origin = 'https://swag.live',
  supportedLanguages = [],
  i18n,
} = {}) => {
  // Filter out invalid FAQ items
  const requiredFields = ['title', 'body', 'created_at', 'updated_at'];
  const validFaqItems = faqItems.filter(item =>
    requiredFields.every(field => item?.[field])
  );
  const { t } = i18n;

  const alternateLanguages = supportedLanguages.filter(
    lang => lang !== DEFAULT_LANGUAGE
  );

  const loadLanguagePromises = alternateLanguages.map(async lang => {
    if (!i18n.hasResourceBundle(lang, TranslationNamespace.SEO)) {
      await i18n.reloadResources(lang, TranslationNamespace.SEO);
    }
  });
  await Promise.all(loadLanguagePromises);

  const faqTitle = t('seo_faq_title', {
    lng: DEFAULT_LANGUAGE,
    ns: TranslationNamespace.SEO,
  });
  const homeTitle = t('seo-home-title', {
    lng: DEFAULT_LANGUAGE,
    ns: TranslationNamespace.SEO,
  });
  const displayTitle = `${faqTitle} - ${homeTitle}`;

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'FAQPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        inLanguage: supportedLanguages.map(lang => ({
          '@type': 'Language',
          name: t(getLanguageTranslationKey({ language: lang }), {
            ns: TranslationNamespace.SEO,
          }),
          alternateName: localeConvertor({ locale: lang, isBCP47: true }),
        })),
        name: {
          '@value': displayTitle,
          '@language': localeConvertor({
            locale: DEFAULT_LANGUAGE,
            isBCP47: true,
          }),
        },
        alternateName: alternateLanguages.map(lang => ({
          '@value': `${t('seo_faq_title', {
            lng: lang,
            ns: TranslationNamespace.SEO,
          })} - ${t('seo-home-title', {
            lng: lang,
            ns: TranslationNamespace.SEO,
          })}`,
          '@language': localeConvertor({ locale: lang, isBCP47: true }),
        })),
        breadcrumb: {
          '@id': `${pageUrl}#breadcrumb`,
        },
        mainEntity: validFaqItems.map((item, index) => ({
          '@type': 'Question',
          '@id': `${pageUrl}#question-${index + 1}`,
          dateCreated: formatISO(item.created_at * 1000),
          dateModified: formatISO(item.updated_at * 1000),
          name: item.title,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.body,
          },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${pageUrl}#breadcrumb`,
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: {
              '@value': homeTitle,
              '@language': localeConvertor({
                locale: DEFAULT_LANGUAGE,
                isBCP47: true,
              }),
            },
            alternateName: alternateLanguages.map(lang => ({
              '@value': t('seo-home-title', {
                lng: lang,
                ns: TranslationNamespace.SEO,
              }),
              '@language': localeConvertor({ locale: lang, isBCP47: true }),
            })),
            item: {
              '@id': `${origin}/#website`,
            },
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: {
              '@value': displayTitle,
              '@language': localeConvertor({
                locale: DEFAULT_LANGUAGE,
                isBCP47: true,
              }),
            },
            alternateName: alternateLanguages.map(lang => ({
              '@value': `${t('seo_faq_title', {
                lng: lang,
                ns: TranslationNamespace.SEO,
              })} - ${t('seo-home-title', {
                lng: lang,
                ns: TranslationNamespace.SEO,
              })}`,
              '@language': localeConvertor({ locale: lang, isBCP47: true }),
            })),
            item: {
              '@id': `${pageUrl}#webpage`,
            },
          },
        ],
      },
    ],
  };
};

export default generateFAQPageJsonLd;
